import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getSocialPosts, adminCreateSocial, adminDeleteSocial } from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { IconBtn } from "./ArticlesAdmin";
import { Trash2, Heart, MessageCircle, Share2 } from "lucide-react";

const empty = { author: "News-Leader", handle: "@fbnewsleader", text: "", image: "", time: "now", sponsored: false, likes: 0, comments: 0, shares: 0 };

export default function SocialAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => getSocialPosts().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return toast.error("Post text is required.");
    setBusy(true);
    try {
      await adminCreateSocial({ ...form, likes: Number(form.likes) || 0, comments: Number(form.comments) || 0, shares: Number(form.shares) || 0 });
      toast.success("Post added to feed."); setOpen(false); setForm(empty); load();
    } catch { toast.error("Could not save post."); }
    finally { setBusy(false); }
  };
  const remove = async (p) => { if (!window.confirm("Delete this post?")) return; await adminDeleteSocial(p.id); toast.success("Post deleted."); load(); };

  return (
    <div>
      <Toolbar title="Social Feed" subtitle="Curate the Facebook-style feed, including co-sponsored advertiser posts" onAdd={() => { setForm(empty); setOpen(true); }} addLabel="New Post" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <Card key={p.id} data-testid={`admin-social-${p.id}`} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--navy)] text-[10px] font-bold text-white">{p.author.split(" ").map((w) => w[0]).join("").slice(0, 2)}</span>
                <div>
                  <p className="text-sm font-bold text-[var(--navy)]">{p.author}</p>
                  <p className="text-[11px] text-slate-500">{p.handle} · {p.time}</p>
                </div>
              </div>
              <IconBtn onClick={() => remove(p)} danger testid={`delete-social-${p.id}`}><Trash2 size={14} /></IconBtn>
            </div>
            {p.sponsored && <span className="mt-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Co-Sponsored</span>}
            <p className="mt-2 text-sm text-slate-700 line-clamp-4">{p.text}</p>
            {p.image && <img src={p.image} alt="" className="mt-2 h-24 w-full rounded object-cover" />}
            <div className="mt-2 flex gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Heart size={12} /> {p.likes}</span>
              <span className="flex items-center gap-1"><MessageCircle size={12} /> {p.comments}</span>
              <span className="flex items-center gap-1"><Share2 size={12} /> {p.shares}</span>
            </div>
          </Card>
        ))}
      </div>

      {open && (
        <Modal title="New Social Post" onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel="Add to Feed">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Author"><input data-testid="soc-author" value={form.author} onChange={set("author")} className={inputCls} /></Field>
            <Field label="Handle"><input value={form.handle} onChange={set("handle")} className={inputCls} /></Field>
            <Field label="Post text" full><textarea data-testid="soc-text" value={form.text} onChange={set("text")} rows={3} className={inputCls} /></Field>
            <Field label="Image URL (optional)" full><input value={form.image} onChange={set("image")} className={inputCls} /></Field>
            <Field label="Time label"><input value={form.time} onChange={set("time")} className={inputCls} placeholder="2h / 1d" /></Field>
            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
              <Field label="Likes"><input type="number" value={form.likes} onChange={set("likes")} className={inputCls} /></Field>
              <Field label="Comments"><input type="number" value={form.comments} onChange={set("comments")} className={inputCls} /></Field>
              <Field label="Shares"><input type="number" value={form.shares} onChange={set("shares")} className={inputCls} /></Field>
            </div>
            <div className="sm:col-span-2"><Check label="Co-Sponsored advertiser post" checked={form.sponsored} onChange={toggle("sponsored")} testid="soc-sponsored" /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
